// Rock details slide-over. 540px right panel with tabs across the top
// (Overview / Milestones / To-dos / Activity). The Overview tab has
// status update widget with RAG composer, owner block, team block,
// progress block, dates block. Milestones tab is an inline editor.
// To-dos shows linked items. Activity shows the recent feed.
//
// Replicates the Drillcut RockDetailsPanel — its 16-file sub-tree is
// flattened into one comprehensive file here, with the same surface
// area.

import { useState } from "react";
import {
  Activity as ActivityIcon,
  Archive,
  CheckCheck,
  CheckSquare,
  Flag,
  Layout,
  Plus,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  formatDateShort,
  isOverdue,
  nextQuarter,
  prevQuarter,
  ragClass,
  statusChipClass,
} from "../utils";
import { Avatar, AvatarStack } from "../components/Avatar";
import { ActivityFeed } from "../components/ActivityFeed";
import {
  InlineEditableText,
  InlineEditableMultiline,
  InlineEditableRichText,
} from "../components/InlineEditable";
import { KebabMenu } from "../components/KebabMenu";
import { DC } from "./createSlideOver/primitives";
import {
  ROCK_STATUS,
  ROCK_STATUS_UPDATE_RAG,
  type Milestone,
  type Rock,
} from "../../../../../../shared/team-planner/schemas";

type RockTab = "overview" | "milestones" | "todos" | "activity";

interface RockSheetProps {
  id: string;
  /** Not used internally any more — kept for source compatibility with
   *  the kanban that still passes the quarter context. */
  quarter: string;
  /** Pre-select a tab when the sheet opens — used by the new
   *  Milestones tab to deep-link from a milestone kanban card back to
   *  its parent rock's Milestones pane. */
  initialTab?: RockTab;
  onClose: () => void;
  onChanged: () => void;
}

export function RockSheet({ id, initialTab, onClose, onChanged }: RockSheetProps) {
  const rockRes = useResource(() => tpApi.getRock(id), [id]);
  const rock = rockRes.data;
  const milestones = useResource(() => tpApi.listMilestones(id), [id]);
  const statusUpdates = useResource(() => tpApi.listRockStatusUpdates(id), [id]);
  const [tab, setTab] = useState<RockTab>(initialTab ?? "overview");
  const [composerOpen, setComposerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function changeStatus(next: string) {
    setBusy(true);
    try {
      await tpApi.setRockStatus(id, next);
      rockRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function patch(input: Record<string, unknown>) {
    setBusy(true);
    try {
      await tpApi.updateRock(id, input);
      rockRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="tp-sheet-backdrop" onClick={onClose} aria-hidden />
      <aside className="tp-sheet" role="dialog" aria-modal="true">
        <div style={{ height: 4, background: "var(--bsp-blue)", flex: "0 0 4px" }} />
        <header className="tp-sheet-header" style={{ flexDirection: "column", alignItems: "stretch", paddingBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--bsp-blue-tint)",
                color: "var(--bsp-blue)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Target size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: "700 10px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: DC.grey,
                  marginBottom: 3,
                }}
              >
                Rock · R{rock?.seq ?? "—"}
              </div>
              {rock ? (
                <InlineEditableText
                  value={rock.title}
                  required
                  maxLength={300}
                  readStyle={{
                    font: "700 16px/1.2 var(--bsp-font-display)",
                    letterSpacing: "0.01em",
                    color: DC.dark,
                    textTransform: "uppercase",
                  }}
                  onCommit={(next) => patch({ title: next })}
                />
              ) : (
                <div style={{ font: "700 16px/1.2 var(--bsp-font-display)", color: DC.dark }}>
                  Loading…
                </div>
              )}
            </div>
            {rock ? (
              <KebabMenu
                actions={[
                  {
                    label: "Mark complete",
                    icon: <CheckCheck size={14} />,
                    disabled: rock.status === "done",
                    onClick: () => changeStatus("done"),
                  },
                  { label: "_divider", divider: true, onClick: () => undefined },
                  {
                    label: "Archive rock",
                    icon: <Archive size={14} />,
                    danger: true,
                    onClick: async () => {
                      if (!window.confirm("Archive this rock? It will be hidden from the kanban but can be restored from an admin view.")) return;
                      await tpApi.archiveRock(rock.id);
                      onChanged();
                      onClose();
                    },
                  },
                ]}
              />
            ) : null}
            <button type="button" className="tp-icon-action" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>

          {rock ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <span className={statusChipClass(rock.status)}>
                {rock.status.replace("_", " ")}
              </span>
              {rock.parentTeam ? (
                <span className="bsp-chip bsp-chip-blue">{rock.parentTeam.name}</span>
              ) : null}
              <span
                style={{
                  fontSize: 12,
                  color: isOverdue(rock.dueAt, rock.status) ? DC.redFg : DC.grey,
                }}
              >
                {rock.quarter} · Due {formatDateShort(rock.dueAt)}
              </span>
              {rock.watchers.length > 0 ? (
                <span style={{ marginLeft: "auto" }}>
                  <AvatarStack owners={rock.watchers} total={rock.watcherCount} max={3} />
                </span>
              ) : null}
            </div>
          ) : null}
        </header>

        <nav
          role="tablist"
          aria-label="Rock details sections"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 22px",
            borderBottom: `1px solid ${DC.border}`,
            flexShrink: 0,
            background: DC.surface,
          }}
        >
          {(
            [
              { id: "overview", label: "Overview", icon: <Layout size={13} /> },
              { id: "milestones", label: "Milestones", icon: <Flag size={13} />, count: milestones.data?.length ?? 0 },
              { id: "todos", label: "To-dos", icon: <CheckSquare size={13} /> },
              { id: "activity", label: "Activity", icon: <ActivityIcon size={13} /> },
            ] as Array<{ id: RockTab; label: string; icon: React.ReactNode; count?: number }>
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                style={{
                  padding: "11px 12px",
                  border: 0,
                  background: "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  font: "700 11px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: active ? "var(--bsp-blue)" : DC.grey,
                  borderBottom: `2px solid ${active ? "var(--bsp-blue)" : "transparent"}`,
                  marginBottom: -1,
                  cursor: "pointer",
                }}
              >
                {t.icon}
                <span>{t.label}</span>
                {typeof t.count === "number" && t.count > 0 ? (
                  <span style={countStyle}>{t.count}</span>
                ) : null}
              </button>
            );
          })}
        </nav>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 22px" }}>
          {!rock ? (
            <p style={{ color: DC.grey }}>Loading…</p>
          ) : tab === "overview" ? (
            <OverviewTab
              rock={rock}
              statusUpdates={statusUpdates.data ?? []}
              onPostUpdate={() => setComposerOpen(true)}
              onChangeStatus={changeStatus}
              onPatch={patch}
              busy={busy}
            />
          ) : tab === "milestones" ? (
            <MilestonesTab
              rockId={id}
              milestones={milestones.data ?? []}
              onChanged={() => {
                milestones.refresh();
                rockRes.refresh();
                onChanged();
              }}
            />
          ) : tab === "todos" ? (
            <LinkedTodosTab rockId={id} />
          ) : (
            <ActivityFeed itemKind="rock" itemId={id} />
          )}
        </div>

        {composerOpen ? (
          <StatusUpdateComposer
            rockId={id}
            onClose={() => setComposerOpen(false)}
            onPosted={() => {
              statusUpdates.refresh();
              setComposerOpen(false);
              onChanged();
            }}
          />
        ) : null}
      </aside>
    </>
  );
}

const countStyle: React.CSSProperties = {
  font: "700 9px/1 var(--bsp-font-mono)",
  padding: "2px 5px",
  background: "var(--bsp-surface-muted)",
  borderRadius: 999,
  color: DC.greyDeep,
  letterSpacing: 0,
};

// ── Overview tab ────────────────────────────────────────────────────
function OverviewTab({
  rock,
  statusUpdates,
  onPostUpdate,
  onChangeStatus,
  onPatch,
  busy,
}: {
  rock: Rock;
  statusUpdates: Array<{ id: string; rag: string; note: string; postedAt: Date }>;
  onPostUpdate: () => void;
  onChangeStatus: (s: string) => void;
  onPatch: (input: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const milestonePct =
    rock.milestoneCounts.total === 0
      ? rock.progressPercent
      : Math.round((rock.milestoneCounts.done / rock.milestoneCounts.total) * 100);
  const latest = statusUpdates[0];
  const previous = statusUpdates.slice(1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Block title="Latest status update" action={
        <button type="button" className="bsp-btn bsp-btn-primary bsp-btn-sm" onClick={onPostUpdate}>
          <Plus size={12} /> Post weekly update
        </button>
      }>
        {!latest ? (
          <div style={{ fontSize: 12, color: DC.grey, fontStyle: "italic" }}>
            No status update yet — post one to kick off the weekly ritual.
          </div>
        ) : (
          <StatusUpdateRow update={latest} latest />
        )}
        {previous.length > 0 ? (
          <div style={{ paddingTop: 10, marginTop: 10, borderTop: `1px solid ${DC.border}` }}>
            <div
              style={{
                font: "700 10px/1 var(--bsp-font-sans)",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: DC.grey,
                marginBottom: 6,
              }}
            >
              Previous updates
            </div>
            {previous.map((u) => (
              <StatusUpdateRow key={u.id} update={u} />
            ))}
          </div>
        ) : null}
      </Block>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Block title="Progress">
          <div style={{ font: "700 24px/1 var(--bsp-font-display)", color: DC.dark }}>
            {milestonePct}%
          </div>
          <div className="tp-card-progress" style={{ marginTop: 8 }}>
            <div className="tp-card-progress-fill" style={{ width: `${milestonePct}%` }} />
          </div>
          <div style={{ fontSize: 11, color: DC.grey, marginTop: 6 }}>
            {rock.milestoneCounts.done} of {rock.milestoneCounts.total} milestones done
          </div>
        </Block>
        <Block title="Status">
          <select
            className="bsp-select"
            value={rock.status}
            disabled={busy}
            onChange={(e) => onChangeStatus(e.target.value)}
          >
            {ROCK_STATUS.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </Block>
      </div>

      <Block title="People">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Row label="Owner">
            {rock.primaryOwner ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Avatar owner={rock.primaryOwner} />
                <span>{rock.primaryOwner.displayName}</span>
              </span>
            ) : (
              <span style={{ color: DC.grey }}>Unassigned</span>
            )}
          </Row>
          <Row label="Watchers">
            {rock.watchers.length > 0 ? (
              <AvatarStack owners={rock.watchers} total={rock.watcherCount} max={5} />
            ) : (
              <span style={{ color: DC.grey, fontSize: 12 }}>No watchers yet</span>
            )}
          </Row>
          {rock.parentTeam ? (
            <Row label="Team">
              <span className="bsp-chip bsp-chip-blue">{rock.parentTeam.name}</span>
            </Row>
          ) : null}
        </div>
      </Block>

      <Block title="Dates">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Row label="Quarter">
            <span className="bsp-mono">{rock.quarter}</span>
          </Row>
          <Row label="Due">
            <input
              type="date"
              className="bsp-input"
              value={rock.dueAt.toISOString().slice(0, 10)}
              onChange={(e) =>
                onPatch({ dueAt: new Date(e.target.value).toISOString() })
              }
              disabled={busy}
            />
          </Row>
        </div>
      </Block>

      <Block title="Description">
        <InlineEditableRichText
          value={rock.description ?? ""}
          placeholder="Click to describe what done looks like…"
          rows={4}
          onCommit={(next) =>
            onPatch({ description: next.trim() === "" ? null : next })
          }
        />
      </Block>

      <Block title="Quarter">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary bsp-btn-icon"
            onClick={() => onPatch({ quarter: prevQuarter(rock.quarter) })}
            aria-label="Previous quarter"
            disabled={busy}
          >
            ‹
          </button>
          <span
            className="bsp-mono"
            style={{
              padding: "6px 12px",
              border: `1px solid ${DC.border}`,
              borderRadius: 4,
              background: DC.surface,
              font: "700 12px/1 var(--bsp-font-mono)",
              letterSpacing: "0.04em",
              color: DC.dark,
              flex: 1,
              textAlign: "center",
            }}
          >
            {rock.quarter}
          </span>
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary bsp-btn-icon"
            onClick={() => onPatch({ quarter: nextQuarter(rock.quarter) })}
            aria-label="Next quarter"
            disabled={busy}
          >
            ›
          </button>
        </div>
      </Block>
    </div>
  );
}

function StatusUpdateRow({
  update,
  latest,
}: {
  update: { rag: string; note: string; postedAt: Date };
  latest?: boolean;
}) {
  const rag = (update.rag === "on_track" ? "green" : update.rag === "at_risk" ? "amber" : "red") as
    | "green" | "amber" | "red";
  return (
    <div style={{ paddingBottom: latest ? 0 : 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span className={ragClass(rag)}>{update.rag.replace("_", " ")}</span>
        <span style={{ fontSize: 11, color: DC.grey }}>{formatDateShort(update.postedAt)}</span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{update.note}</div>
    </div>
  );
}

// ── Milestones tab ──────────────────────────────────────────────────
function MilestonesTab({
  rockId,
  milestones,
  onChanged,
}: {
  rockId: string;
  milestones: Milestone[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDue, setDraftDue] = useState(new Date().toISOString().slice(0, 10));

  async function add() {
    if (!draftTitle.trim()) return;
    await tpApi.createMilestone({
      rockId,
      title: draftTitle.trim(),
      dueAt: new Date(draftDue).toISOString(),
    });
    setDraftTitle("");
    setAdding(false);
    onChanged();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {milestones.length === 0 && !adding ? (
        <div style={{ color: DC.grey, fontSize: 13 }}>No milestones yet.</div>
      ) : null}
      {milestones.map((m, idx) => (
        <div
          key={m.id}
          style={{
            display: "grid",
            gridTemplateColumns: "28px 24px 1fr 130px 28px",
            gap: 8,
            alignItems: "center",
            padding: "10px 12px",
            background: DC.surface,
            border: `1px solid ${DC.border}`,
            borderLeft: "3px solid var(--bsp-green)",
            borderRadius: 6,
          }}
        >
          <span style={{ font: "800 10px/1 var(--bsp-font-sans)", color: DC.grey, textAlign: "center" }}>
            M{idx + 1}
          </span>
          <input
            type="checkbox"
            checked={m.status === "done"}
            onChange={async () => {
              await tpApi.updateMilestone(m.id, {
                status: m.status === "done" ? "open" : "done",
              });
              onChanged();
            }}
          />
          <input
            defaultValue={m.title}
            onBlur={async (e) => {
              if (e.target.value.trim() && e.target.value !== m.title) {
                await tpApi.updateMilestone(m.id, { title: e.target.value.trim() });
                onChanged();
              }
            }}
            style={{
              border: 0,
              background: "transparent",
              outline: 0,
              font: "400 13px/1 var(--bsp-font-sans)",
              color: m.status === "done" ? DC.grey : "var(--bsp-fg)",
              textDecoration: m.status === "done" ? "line-through" : undefined,
            }}
          />
          <input
            type="date"
            defaultValue={m.dueAt.toISOString().slice(0, 10)}
            onBlur={async (e) => {
              if (e.target.value) {
                await tpApi.updateMilestone(m.id, { dueAt: new Date(e.target.value).toISOString() });
                onChanged();
              }
            }}
            style={{
              border: 0,
              background: "transparent",
              outline: 0,
              font: "400 12px/1 var(--bsp-font-mono)",
              color: DC.grey,
            }}
          />
          <button
            type="button"
            title="Delete milestone"
            onClick={async () => {
              if (!window.confirm("Delete this milestone?")) return;
              await tpApi.deleteMilestone(m.id);
              onChanged();
            }}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 24, height: 24, border: 0, background: "transparent",
              color: DC.grey, borderRadius: 4, cursor: "pointer",
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      {adding ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 28px", gap: 8 }}>
          <input
            className="bsp-input"
            placeholder="Checkpoint title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            autoFocus
          />
          <input
            type="date"
            className="bsp-input"
            value={draftDue}
            onChange={(e) => setDraftDue(e.target.value)}
          />
          <button
            type="button"
            className="bsp-btn bsp-btn-primary bsp-btn-icon"
            onClick={add}
          >
            <Plus size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-sm"
          onClick={() => setAdding(true)}
        >
          <Plus size={13} /> Add milestone
        </button>
      )}
    </div>
  );
}

// ── Linked to-dos tab ───────────────────────────────────────────────
function LinkedTodosTab({ rockId }: { rockId: string }) {
  const todos = useResource(async () => {
    const list = await tpApi.listTodos("all");
    return list.filter((t) => t.rockId === rockId);
  }, [rockId]);

  if (todos.loading) return <p style={{ color: DC.grey }}>Loading…</p>;
  if (!todos.data || todos.data.length === 0) {
    return <p style={{ color: DC.grey }}>No linked to-dos.</p>;
  }
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {todos.data.map((t) => (
        <li
          key={t.id}
          style={{
            display: "grid",
            gridTemplateColumns: "60px 1fr auto auto",
            gap: 10,
            alignItems: "center",
            padding: "10px 12px",
            background: DC.surface,
            border: `1px solid ${DC.border}`,
            borderRadius: 6,
          }}
        >
          <span className="bsp-mono" style={{ fontSize: 11, color: DC.grey }}>T{t.seq}</span>
          <span style={{ fontSize: 13 }}>{t.title}</span>
          <span className={statusChipClass(t.status)}>{t.status.replace("_", " ")}</span>
          <span style={{ fontSize: 11, color: DC.grey }}>{formatDateShort(t.dueAt)}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Status update composer ──────────────────────────────────────────
function StatusUpdateComposer({
  rockId,
  onClose,
  onPosted,
}: {
  rockId: string;
  onClose: () => void;
  onPosted: () => void;
}) {
  const [rag, setRag] = useState<(typeof ROCK_STATUS_UPDATE_RAG)[number]>("on_track");
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const RAG_LABEL = {
    on_track: "On track",
    at_risk: "At risk",
    off_track: "Off track",
  } as const;
  const RAG_TINT = {
    on_track: "var(--bsp-green-tint)",
    at_risk: "var(--bsp-orange-tint)",
    off_track: "var(--bsp-red-tint)",
  } as const;
  const RAG_FG = {
    on_track: "var(--bsp-green-fg)",
    at_risk: "var(--bsp-orange-fg)",
    off_track: "var(--bsp-red-fg)",
  } as const;

  async function post() {
    if (!note.trim()) return;
    setPending(true);
    try {
      await tpApi.postRockStatusUpdate(rockId, { rag, note: note.trim() });
      onPosted();
    } finally {
      setPending(false);
    }
  }
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(20,24,28,0.45)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 80,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: DC.surface,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          padding: 22,
          boxShadow: "var(--bsp-shadow-pop)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div
            style={{
              font: "700 14px/1 var(--bsp-font-display)",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              flex: 1,
            }}
          >
            Post weekly update
          </div>
          <button type="button" className="tp-icon-action" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {ROCK_STATUS_UPDATE_RAG.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRag(r)}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: `1px solid ${rag === r ? RAG_FG[r] : DC.borderStrong}`,
                background: rag === r ? RAG_TINT[r] : DC.surface,
                color: rag === r ? RAG_FG[r] : DC.grey,
                borderRadius: 6,
                font: "700 11px/1 var(--bsp-font-sans)",
                letterSpacing: "0.04em",
                cursor: "pointer",
              }}
            >
              {RAG_LABEL[r]}
            </button>
          ))}
        </div>
        <textarea
          className="bsp-input"
          style={{ minHeight: 100, padding: 10, lineHeight: 1.4 }}
          placeholder="What's the headline? Wins, blockers, asks…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" className="bsp-btn bsp-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            disabled={pending || !note.trim()}
            onClick={post}
          >
            {pending ? "Posting…" : "Post update"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Block primitive ─────────────────────────────────────────────────
function Block({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: DC.surface,
        border: `1px solid ${DC.border}`,
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            flex: 1,
            font: "700 10px/1 var(--bsp-font-sans)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: DC.grey,
          }}
        >
          {title}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          minWidth: 70,
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: DC.grey,
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, fontSize: 13 }}>{children}</span>
    </div>
  );
}
